import { useState, useMemo, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAuthentication, useDialog, useRestaurant, useCart } from "hooks";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
 BasePage,
 ShareButton,
 NotificationTypography,
 LikeChip,
 DealGrid,
 ReviewList,
 RatingTypography,
 ChipButton,
 OpenHoursTypography,
 RestaurantMap,
 ChatButton,
} from "components";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CallIcon from "@mui/icons-material/Call";
import DangerousIcon from "@mui/icons-material/Dangerous";
import PublicIcon from "@mui/icons-material/Public";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
 Box,
 Grid,
 Card,
 CardHeader,
 CardContent,
 Typography,
 Tab,
 Chip,
 Skeleton,
 Avatar,
 CardMedia,
 Alert,
 Button,
} from "@mui/material";
import { RestaurantPageSkeleton } from "./skeleton";
import { getStatusWarningMessage } from "utilities";
import { API_ENDPOINTS } from "constants";

export function RestaurantPage() {
 const location = useLocation();
 const { confirm } = useDialog();
 const { restaurantId } = useParams();
 const { user } = useAuthentication();
 const { cart, clearCart, openCheckout } = useCart();
 const [tab, setTab] = useState("deals");
 const {
  restaurant,
  loading,
  error,
  likeRestaurant,
  unlikeRestaurant,
  locations,
  locationsLoading,
 } = useRestaurant(restaurantId);

 useEffect(() => {
  window.scrollTo(0, 0);
 }, [location.pathname]);

 const handleTabChange = (_, newTab) => {
  setTab(newTab);
 };

 const handleLikeClick = async (event) => {
  event.preventDefault();
  event.stopPropagation();

  if (!user) {
   const result = await confirm({
    title: "Sign in required",
    description: `Sign in to add to favourites?`,
    confirmLabel: "Sign in",
    cancelLabel: "Cancel",
   });
   if (result) {
    window.location.href = API_ENDPOINTS.AUTH.SIGN_IN;
   }
   return;
  }

  if (liked) {
   unlikeRestaurant(restaurant.id, user.uid);
  } else {
   likeRestaurant(restaurant.id, user.uid);
  }
 };

 // Show cart conflict banner when user has items from a different restaurant
 const cartItemCount = Object.values(cart.items || {}).reduce((a, b) => a + b, 0);
 const hasConflict =
  cartItemCount > 0 &&
  cart.restaurantId &&
  cart.restaurantId !== restaurantId;

 if (loading) return <RestaurantPageSkeleton />;

 if (error)
  return (
   <BasePage>
    <NotificationTypography
     color="error"
     Icon={DangerousIcon}
     title="Something went wrong"
     message="Please try again"
     showActions={true}
     backTo="/search"
    />
   </BasePage>
  );

 if (!restaurant)
  return (
   <BasePage>
    <NotificationTypography
     color="error"
     Icon={SearchOffIcon}
     title="Restaurant Not Found"
     message="This restaurant may not exist or has been removed."
     showActions={true}
     backTo="/search"
    />
   </BasePage>
  );

 const liked = user && restaurant.likedBy?.includes(user.uid);
 const isOwner = user?.uid === restaurant.userId;
 const isActive = restaurant.status === "active";
 const isPending =
  isOwner &&
  restaurant.status &&
  ["pendingCreate", "pendingEdit", "pendingDelete"].includes(restaurant.status);

 if (isActive || isPending) {
  return (
   <BasePage
    showBreadcrumbs={true}
    breadcrumbProps={{
     restaurantId: restaurant.id,
     restaurantDisplayName: restaurant.name,
    }}
   >
    <Grid container spacing={4}>
     <Box sx={{ position: "sticky", top: "96px", zIndex: 1000, width: "100%" }}>

      {/* Cart conflict banner */}
      {hasConflict && (
       <Alert
        severity="info"
        icon={<ShoppingCartIcon fontSize="inherit" />}
        sx={{ borderRadius: "8px", width: "100%", mb: 1 }}
        action={
         <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button size="small" color="inherit" variant="outlined" onClick={openCheckout}>
           View cart
          </Button>
          <Button size="small" color="inherit" onClick={clearCart}>
           Clear
          </Button>
         </Box>
        }
       >
        You have {cartItemCount} item{cartItemCount !== 1 ? "s" : ""} in your cart from{" "}
        <strong>{cart.restaurantMeta?.name || "another restaurant"}</strong>.
        Adding from here will ask you to replace it.
       </Alert>
      )}

      {/* Lifecycle warnings */}
      {user?.uid === restaurant.userId &&
       restaurant.status &&
       ["pendingCreate", "pendingEdit", "pendingDelete"].includes(restaurant.status) && (
        <Alert severity="warning" sx={{ borderRadius: "8px", width: "100%" }}>
         {getStatusWarningMessage(restaurant.status, "restaurant")}
        </Alert>
       )}

      {user?.uid === restaurant.userId && restaurant.status === "rejected" && (
       <Alert severity="error" sx={{ borderRadius: "8px", width: "100%" }}>
        <strong>Your restaurant submission was rejected.</strong>
        <div>
         <strong>Reason:</strong>{" "}
         {restaurant.statusHistory?.slice().reverse().find((h) => h.reason)?.reason ||
          restaurant.rejectionReason ||
          "No reason provided."}
        </div>
        <div>You can edit your restaurant and resubmit for approval.</div>
       </Alert>
      )}

      {restaurant.temporarilyClosed && (
       <Alert severity="error" sx={{ borderRadius: "8px", width: "100%" }}>
        This restaurant is temporarily closed.{" "}
        {user?.uid === restaurant.userId ? `Customers` : `You`} can still see the restaurant,
        but it is not currently operating.
       </Alert>
      )}
     </Box>

     {restaurant.coverUrl && restaurant.coverUrl.trim() !== "" && (
      <Grid size={12}>
       <CardMedia
        component="img"
        image={restaurant.coverUrl}
        alt={`${restaurant.name} cover`}
        sx={{
         width: "100%",
         height: { xs: 50, sm: 100, md: 150 },
         objectFit: "cover",
         borderRadius: "12px",
        }}
       />
      </Grid>
     )}
     <Grid size={{ xs: 12, md: 6 }}>
      {locationsLoading ? (
       <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: "12px" }} />
      ) : (
       <RestaurantMap locations={locations} />
      )}
     </Grid>
     <Grid size={{ xs: 12, md: 6 }}>
      <Card sx={{ height: { xs: "auto", md: "400px", borderRadius: "12px" } }}>
       <CardHeader
        sx={{ paddingBottom: 0 }}
        avatar={
         <Avatar
          aria-label="restaurant"
          src={restaurant.logoUrl && restaurant.logoUrl.trim() !== "" ? restaurant.logoUrl : undefined}
         >
          {(!restaurant.logoUrl || restaurant.logoUrl.trim() === "") &&
           restaurant.name?.charAt(0).toUpperCase()}
         </Avatar>
        }
        action={
         <Box>
          <LikeChip count={restaurant.likedBy?.length || 0} liked={liked} onClick={handleLikeClick} />
          <ShareButton restaurantId={restaurant.id} />
          <ChatButton
           otherUser={{
            id: restaurant.userId,
            type: "vendor",
            name:
             restaurant.userFirstName && restaurant.userLastName
              ? `${restaurant.userFirstName} ${restaurant.userLastName}`
              : restaurant.name,
           }}
           context="restaurant"
           contextData={{ restaurantId: restaurant.id, restaurantName: restaurant.name }}
           disabled={isOwner}
          />
         </Box>
        }
        title={restaurant.name}
       />
       <CardContent sx={{ paddingTop: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        {restaurant.description && (
         <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", mt: 1 }}>
          "{restaurant.description}"
         </Typography>
        )}
        {restaurant.phoneNumber && (
         <Typography
          component="a"
          href={`tel:${restaurant.phoneNumber}`}
          variant="body2"
          color="text.secondary"
          sx={{ display: "inline-flex", alignItems: "center", mb: 0.5, textDecoration: "none", "&:hover": { color: "primary.main", textDecoration: "underline" } }}
         >
          <CallIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
          {restaurant.phoneNumber}
         </Typography>
        )}
        {restaurant.websiteUrl && (
         <Typography
          component="a"
          href={restaurant.websiteUrl.startsWith("http") ? restaurant.websiteUrl : `https://${restaurant.websiteUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          color="text.secondary"
          sx={{ display: "inline-flex", alignItems: "center", mb: 0.5, textDecoration: "none", "&:hover": { color: "primary.main", textDecoration: "underline" } }}
         >
          <PublicIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
          {restaurant.websiteUrl}
         </Typography>
        )}
        <RatingTypography reviews={restaurant.reviewCount ?? 0} rating={restaurant.rating} editable={false} page={true} />
        {restaurant.temporarilyClosed ? (
         <Typography variant="body2" color="error" sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
          <AccessTimeIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Box component="span" sx={{ fontWeight: 500 }}>Temporarily Closed</Box>
         </Typography>
        ) : (
         <OpenHoursTypography restaurant={restaurant} />
        )}
        {restaurant.dietaryPreferences?.length > 0 && (
         <Box display="flex" flexWrap="wrap">
          {restaurant.dietaryPreferences.map((preference, index) => (
           <Box key={index} mr={1} mb={1}>
            <Chip component="span" size="small" label={preference} />
           </Box>
          ))}
         </Box>
        )}
        {locations.length > 0 && <ChipButton locations={locations} />}
       </CardContent>
      </Card>
     </Grid>
     <Grid size={12}>
      <TabContext value={tab}>
       <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <TabList onChange={handleTabChange}>
         <Tab label="Deals" value="deals" />
         <Tab label="Reviews" value="reviews" />
        </TabList>
       </Box>
       <TabPanel value="deals" sx={{ px: 0 }}>
        <RestaurantDealsGrid restaurantId={restaurantId} isOwner={isOwner} ownerId={user?.uid} />
       </TabPanel>
       <TabPanel value="reviews" sx={{ px: 0 }}>
        <RestaurantReviewsTab restaurant={restaurant} isOwner={isOwner} userId={user?.uid} />
       </TabPanel>
      </TabContext>
     </Grid>
    </Grid>
   </BasePage>
  );
 }

 return (
  <BasePage>
   <NotificationTypography
    color="error"
    Icon={SearchOffIcon}
    title="Restaurant Not Found"
    message="This restaurant may not exist or has been removed."
    showActions={true}
    backTo="/search"
   />
  </BasePage>
 );
}

function RestaurantDealsGrid({ restaurantId, isOwner, ownerId }) {
 const filters = useMemo(
  () => ({ restaurantId, isOwner, ownerId, filterDay: "all" }),
  [restaurantId, isOwner, ownerId],
 );
 return <DealGrid sortBy="closestTime" filters={filters} hideWeekButtons={true} />;
}

function RestaurantReviewsTab({ restaurant, isOwner, userId }) {
 const mockDeal = {
  id: restaurant.id,
  restaurantId: restaurant.id,
  restaurantName: restaurant.name,
  restaurantLogoUrl: restaurant.logoUrl || null,
  title: restaurant.name,
  userId: restaurant.userId || null,
 };
 return <ReviewList deal={mockDeal} type="restaurant" isOwner={isOwner} userId={userId} />;
}
